<template>
  <BittsLoading :is-loading="loadingData">
    <div
      v-if="rows.length"
      :class="isOpenData && 'population-preview--open-data'"
    >
      <BittsTablePro
        :columns="columnDefinitions"
        :compress-columns="true"
        :pagination-page-size="20"
        :rows="rows"
        :bordered="true"
        :pagination="!isOpenData"
        :shrink-title="true"
      />
      <div v-if="isOpenData" class="population-preview__open-data-footer">
        <span
          class="text-neutral-text-strong font-semibold text-lg tracking-[-0.2px]"
        >
          Just a preview
        </span>
        <span class="text-neutral-text text-sm">
          Showing {{ rows.length }} of
          {{ intComma(populationPreviewCount) }} accounts. More coming soon.
        </span>
      </div>
      <p v-else class="flex items-center pt-12 text-neutral-text-weak text-sm">
        <FontAwesomeIcon
          :icon="['fak', 'information-circle']"
          :style="{ height: '12px', width: '12px', color: 'currentColor' }"
          class="mr-6"
        />
        <span>Preview is limited to 100 records</span>
      </p>
    </div>
    <div v-else class="flex items-center justify-center min-h-[600px]">
      <BittsEmptyState
        :title="emptyStateTitle"
        :description="emptyStateDescription"
        svg-name="emptyStatePopulations"
        class="border-none bg-neutral-background-weak"
      />
    </div>
  </BittsLoading>
</template>

<script lang="ts">
export const OPEN_DATA_PREVIEW_LIMIT = 10;
</script>

<script setup lang="ts">
import { BittsEmptyState, BittsLoading, BittsTablePro } from '@crossbeam/bitts';

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { intComma } from 'humanize-plus';
import { omit } from 'lodash';
import { computed, onMounted, ref } from 'vue';
import type { Component } from 'vue';

import PopulationTableCell from '@/components/populations/PopulationTableCell.vue';
import PopulationTableRecordCell from '@/components/populations/PopulationTableRecordCell.vue';

import usePopulations from '@/composables/usePopulations';
import { ACCOUNT_OWNER_EMAIL_DISPLAY, MDM_PROPERTIES } from '@/constants/mdm';
import {
  PopPreviewFieldItem,
  PopPreviewResponse,
  PopPreviewRowItem,
  Population,
  PopulationPreviewPayload,
  PopulationStats,
} from '@/types/populations';

const {
  population,
  sourceFilterExpressions,
  offlinePartnerUuid,
  isSyncing,
  isOpenData,
} = defineProps<{
  population:
    | (Population & {
        meta?: PopulationStats;
        isEmpty?: boolean;
        tag?: string;
      })
    | PopulationPreviewPayload
    | null;

  sourceFilterExpressions: Record<string, unknown>;
  offlinePartnerUuid?: string;
  isSyncing: boolean;
  isOpenData?: boolean;
}>();

const emits = defineEmits<{
  'preview-population-count': [count: number];
  'user-refresh-preview': [];
}>();

const { INITIAL_DATA, previewPopulation, previewPopulationCount } =
  usePopulations(
    null,
    (offlinePartnerUuid ? offlinePartnerUuid : null) as null | undefined,
    null,
  );

type PopulationTableDef = {
  data: PopPreviewFieldItem;
  resizable?: boolean;
  field: string;
  suppressMovable?: boolean;
  cellRenderer?: Component;
  minWidth?: number;
  sortable?: boolean;
  initialSort?: 'asc' | 'desc';
  comparator?: (cellA: { val: string }, cellB: { val: string }) => number;
  pinned?: 'left' | 'right';
};

const data = ref<{
  fields: PopPreviewFieldItem[];
  rows: PopPreviewRowItem[];
}>(INITIAL_DATA);
const populationPreviewCount = ref(0);
const populationPreviewCountTimeout = ref(false);
const loadingData = ref(true);

async function onPreviewPopulation() {
  loadingData.value = true;
  const isValidSource =
    population?.base_schema && population?.base_table && population?.source_id;
  if (!isValidSource) {
    data.value = INITIAL_DATA;
    loadingData.value = false;
    return;
  }
  const result = await previewPopulation(population, sourceFilterExpressions);
  data.value = result as PopPreviewResponse;
  loadingData.value = false;
  const { count, timeout, error } = await previewPopulationCount(
    population,
    sourceFilterExpressions,
  );
  populationPreviewCount.value = count;
  populationPreviewCountTimeout.value = timeout;
  if (!timeout && !error) {
    emits('preview-population-count', count);
  }
}

onMounted(async () => {
  await onPreviewPopulation();
});

const cellRenderer = computed(() => PopulationTableRecordCell);

const columns = computed(() => {
  if (!data.value.fields) return [];
  return data.value.fields.map((item: PopPreviewFieldItem) => {
    const isFirstColumn =
      item.mdm_property === MDM_PROPERTIES.account.NAME ||
      (item.mdm_property === 'person_email' &&
        item.display_name !== ACCOUNT_OWNER_EMAIL_DISPLAY);
    const obj: PopulationTableDef = {
      data: item,
      resizable: true,
      field: item.display_name,
      cellRenderer: PopulationTableCell,
      suppressMovable: true,
      minWidth: 250,
      sortable: false,
    };
    if (isFirstColumn) {
      obj.pinned = 'left';
      obj.sortable = true;
      obj.initialSort = 'asc';
      /* Offline partners populations should not link to the IRP */
      obj.cellRenderer = cellRenderer.value;
      obj.comparator = (cellA: { val: string }, cellB: { val: string }) => {
        if (cellA.val === cellB.val) return 0;
        return cellA.val > cellB.val ? 1 : -1;
      };
    }
    return obj;
  });
});

const columnDefinitions = computed(() =>
  columns.value.map((col) => omit(col, ['data'])),
);

type RowItem = Record<
  string,
  {
    val: unknown;
    recordId: string;
    sourceId?: number;
    dataType?: string | null;
  }
>;

const allRows = computed(() => {
  if (!data.value.rows) return [];
  return data.value.rows.map((item: PopPreviewRowItem) =>
    item.values.reduce((agg: RowItem, val, i) => {
      const column = columns.value[i];
      if (!column) return agg;
      const columnName = column.field;
      agg[columnName] = {
        val,
        recordId: item.record_id,
        sourceId: column.data.source_id,
        dataType: column.data.pg_data_type,
      };
      return agg;
    }, {}),
  );
});

const rows = computed(() =>
  isOpenData ? allRows.value.slice(0, OPEN_DATA_PREVIEW_LIMIT) : allRows.value,
);

const emptyStateTitle = computed(() =>
  isSyncing ? 'Your data is still syncing' : 'No records match your filters',
);

const emptyStateDescription = computed(() =>
  isSyncing
    ? "We're importing your data source. Preview will be available once syncing is complete."
    : 'Try adjusting your filters then click Refresh Preview to see updated results',
);
</script>
<style lang="pcss" scoped>
@reference "../../../css/theme.css";
.population-preview--open-data {
  @apply bg-white rounded-[10px] border border-neutral-border overflow-clip;
  box-shadow: 0 2px 3px 0 rgba(13, 25, 42, 0.05);

  :deep(.ag-root-wrapper) {
    @apply border-0 rounded-none;
  }
}

.population-preview__open-data-footer {
  @apply flex flex-col items-center text-center gap-4 p-40 border-t border-neutral-border;
  backdrop-filter: blur(2px);
  background-image: linear-gradient(
    to bottom,
    rgba(255, 255, 255, 0),
    theme(colors.neutral-background-weak) 64%
  );
}
</style>
